import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

const reactPlugin = new ReactPlugin();
const connectionString = import.meta.env.VITE_APP_INSIGHTS_CONN_STRING;

if (!connectionString) {
  console.error('Instrumentation key not found in the connection string');
}

/**
 * Sets the telemetry name to be used in AppInsights page name
 */
const setTelemetryName = (pathname: string): string => {
  try {
    if (pathname.includes('category')) {
      const categoryId = pathname.split('/').pop();
      return `Category: ${categoryId}`;
    } else if (pathname.includes('article')) {
      const articleTitle = pathname.split('/').pop();
      return `Article: ${articleTitle}`;
    } else if (pathname.includes('author')) {
      const authorName = pathname.split('/').pop();
      return `Author: ${authorName}`;
    }
    return pathname;
  } catch (error) {
    console.error('Error setting telemetry name:', error);
    return pathname;
  }
};

/**
 * Sets the document title based on the page and route
 */
const setPageTitle = (page: string, route?: 'category' | 'author') => {
  try {
    if (route === 'category') {
      document.title = `${page} | eklipX Research`;
      return;
    }
    if (route === 'author') {
      document.title = `${page} | eklipX Research`;
      return;
    }
    document.title = page;
  } catch (error) {
    console.error('Error setting page title:', error);
  }
};

/**
 * Creates and sends a custom event to AppInsights
 */
const trackCustomEvent = (name: string, properties?: { [key: string]: string }) => {
  try {
    appInsights.trackEvent({ name }, properties);
  } catch (error) {
    console.error('Error tracking custom event:', error);
  }
};

/*
 * Creates and sends an exception to AppInsights
 */
const trackException = (error: Error) => {
  try {
    appInsights.trackException({ error });
  } catch (error) {
    console.error('Error tracking exception:', error);
  }
};

/**
 * Setup and load AppInsights
 */
const appInsights = new ApplicationInsights({
  config: {
    connectionString: connectionString,
    enableAutoRouteTracking: true,
    extensions: [reactPlugin],
  },
});

const initializeAppInsights = () => {
  appInsights.loadAppInsights();
};

/**
 * Custom telemetry initializer to parse into AppInsights
 */
appInsights.addTelemetryInitializer((env: ITelemetryItem) => {
  if (env.baseType === 'PageviewData' || env.baseType === 'PageviewPerformanceData') {
    if (env.baseData) {
      env.baseData.name = setTelemetryName(window.location.pathname);
    }
  }

  env.tags = env.tags || [];
  env.data = env.data || {};
});

export { appInsights, initializeAppInsights, reactPlugin, setPageTitle, trackCustomEvent, trackException };
